import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';

export interface BrowserBarHintProps {
  /** What to click, drawn highlighted at the start of the bar. */
  marker: ReactNode;
  /** What sits next to it: the web address, or the menu item it opens. */
  children: ReactNode;
  className?: string;
}

/**
 * A sketch of the browser's own bar, pointing at the one thing to click.
 *
 * Browser settings are described in words nobody recognises until they see
 * where they live. It is a picture, not a control: the steps beside it carry the meaning.
 */
export function BrowserBarHint({ marker, children, className }: BrowserBarHintProps) {
  return (
    <div
      aria-hidden='true'
      className={cn(
        'flex w-full max-w-90 items-center gap-2.5 rounded-full border border-line bg-surface p-1.5 pr-4 text-sm text-ink-muted',
        className,
      )}
    >
      <span
        data-slot='browser-bar-hint-marker'
        className='flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-accent-tint px-1.5 font-semibold text-ink ring-2 ring-accent'
      >
        {marker}
      </span>
      <span className='truncate'>{children}</span>
    </div>
  );
}
